/* eslint-disable max-params */
import { useState, useRef, useEffect } from 'react'
import type { ReactZoomPanPinchContentRef } from 'react-zoom-pan-pinch'

interface ZoomState {
  index: number
  isZoomed: boolean
}

interface PointerPosition {
  x: number
  y: number
}

export function useImageZoom() {
  const [zoomedImage, setZoomedImage] = useState<ZoomState | null>(null)
  const wrapperRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const pointerRef = useRef<PointerPosition | null>(null)
  const rafRef = useRef<number | null>(null)
  const transformRefs = useRef<Record<number, ReactZoomPanPinchContentRef>>({})
  const isTransitioning = useRef(false)

  const handleZoom = (
    index: number,
    transformRef: ReactZoomPanPinchContentRef,
    onZoomStart?: () => void,
    onZoomComplete?: () => void
  ) => {
    if (!transformRef || isTransitioning.current) {
      return
    }

    isTransitioning.current = true
    onZoomStart?.()

    const scale = 2

    if (zoomedImage?.index === index && zoomedImage.isZoomed) {
      setZoomedImage(null)
      transformRef.setTransform(0, 0, 1, 0)
      isTransitioning.current = false
      onZoomComplete?.()
    } else {
      setZoomedImage({ index, isZoomed: true })
      transformRef.zoomToElement(`image-${index}`, scale, 0)
      isTransitioning.current = false
      onZoomComplete?.()
    }
  }

  const updateTransform = (index: number) => {
    if (isTransitioning.current) {
      return
    }

    const wrapper = wrapperRefs.current[index]
    const ref = transformRefs.current[index]

    if (!wrapper || !ref || !pointerRef.current) {
      return
    }

    const devicePixelRatio = window.devicePixelRatio || 1
    const rect = wrapper.getBoundingClientRect()

    const width = rect.width / devicePixelRatio
    const height = rect.height / devicePixelRatio
    const left = rect.left / devicePixelRatio
    const top = rect.top / devicePixelRatio

    const scale = 2
    const { x: clientX, y: clientY } = pointerRef.current

    const offsetX = clientX / devicePixelRatio - left
    const offsetY = clientY / devicePixelRatio - top
    let x = -(offsetX * scale - width / 2)
    let y = -(offsetY * scale - height / 2)

    const scaledWidth = width * scale
    const scaledHeight = height * scale

    const maxX = 0
    const minX = width - scaledWidth

    x = Math.max(minX, Math.min(maxX, x))

    const maxY = 0
    const minY = height - scaledHeight

    y = Math.max(minY, Math.min(maxY, y))

    ref.setTransform(x, y, scale, 0)
  }

  useEffect(() => {
    const frameId = rafRef.current

    return () => {
      if (frameId != null) {
        cancelAnimationFrame(frameId)
      }

      isTransitioning.current = false
    }
  }, [])

  return {
    zoomedImage,
    wrapperRefs,
    pointerRef,
    rafRef,
    transformRefs,
    handleZoom,
    updateTransform,
  }
}
